/// <amd-module name="Repos/Discussion/Editor/Integrations/BaseDiscussionIntegration" />
import * as React from "react";
import { DiffViewerOrientation } from "Repos/Common/Contracts/VersionControlViewModel";
import {
    IDiffDiscussionWidget,
    IDiffIntegration,
    ILineInfo,
    LineInfoType,
    SpecialCommentPositions
} from "Repos/Discussion/Common/DiscussionContracts";
import { IDiscussionThread, IDiscussionThreadModel, IDiscussionThreadStore } from "Repos/Discussion/Common/DiscussionThread.Contracts";
import {
    IObservableArray,
    IObservableArrayEventArgs,
    IReadonlyObservableArray,
    IReadonlyObservableValue,
    ObservableArray,
    ObservableValue
} from "VSS/Core/Observable";
import { IVssPageContext } from "VSS/Platform/Context";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { Observer } from "VSSUI/Observer";
import { CollapsibleDiscussionHost } from "../Components/CollapsibleDiscussionHost";
import { IDiscussionBaseWidgetProps, IDiscussionWidgetContentProps } from "../Widgets/DiscussionBaseWidget";

export interface IDiscussionIntegrationContext {
    addToOriginalDiff: boolean;
    lineInfo?: ILineInfo;
}

export interface IDiscussionIntegrationProps {
    canAddComments: boolean;
    currentUser: IdentityRef;
    diffOrientation?: IReadonlyObservableValue<DiffViewerOrientation>;
    discussionStore: IDiscussionThreadStore;
    filePath: string;
    pageContext: IVssPageContext;
    createThreadModel: () => IDiscussionThreadModel;
    extractFilePath: (thread: IDiscussionThreadModel) => string | undefined;
    filterThreads: (threads: IDiscussionThreadModel[], filePath: string) => IDiscussionThreadModel[];
    getIntegrationContext: (thread: IDiscussionThreadModel) => IDiscussionIntegrationContext;
    getLineNumber: (thread: IDiscussionThreadModel) => number | undefined;
    initializeThread?: (thread: IDiscussionThreadModel, filePath: string, integrationContext: IDiscussionIntegrationContext) => void;
    isOriginalSide: (thread: IDiscussionThreadModel) => boolean | undefined;
    renderDiscussion: (props: { thread: IDiscussionThreadModel }) => JSX.Element;
    onThreadsUpdated?: () => void;
}

export abstract class BaseDiscussionIntegration<TWidgetType extends IDiffDiscussionWidget, TProps extends IDiscussionIntegrationProps>
    implements IDiffIntegration {
    public get canAddComments(): boolean {
        return this.props.canAddComments;
    }
    private diffWidth: ObservableValue<number | undefined>;
    protected props: TProps;
    protected widgets: IObservableArray<TWidgetType>;

    constructor(props: TProps) {
        this.props = props;
        this.widgets = new ObservableArray<TWidgetType>([]);
    }

    public getThreads(): IDiscussionThread[] {
        const threads = this.props.discussionStore.getDiscussionThreads();
        return this.filterThreads(threads.value).map(t => t.thread);
    }

    public getWidgets(): IReadonlyObservableArray<TWidgetType> {
        return this.widgets;
    }

    public dispose(): void {
        const threads = this.props.discussionStore.getDiscussionThreads();
        threads.unsubscribe(this.onUpdateThreads);
        // @ts-ignore TypeScript 4.0 upgrade
        delete this.props;
    }

    public setDiffWidth(width: ObservableValue<number | undefined>) {
        this.diffWidth = width;
    }

    protected initialize(): void {
        const threads = this.props.discussionStore.getDiscussionThreads();
        threads.subscribe(this.onUpdateThreads);

        const filteredThreads = this.filterThreads(threads.value);
        for (const thread of filteredThreads) {
            // Adding an existing discussion thread
            this.addThreadToWidget(thread);
        }
    }

    protected abstract isMyWidgetInstance(widget: TWidgetType): boolean;
    protected abstract createWidget(widgetProps: IDiscussionBaseWidgetProps<IDiscussionWidgetContentProps>): TWidgetType;

    protected addThreadToWidget(thread: IDiscussionThreadModel) {
        const { currentUser, discussionStore, getIntegrationContext, pageContext, renderDiscussion } = this.props;
        const integrationContext = getIntegrationContext(thread);
        const { addToOriginalDiff, lineInfo } = integrationContext;

        let widget = this.widgets.value.filter(
            w =>
                this.isMyWidgetInstance(w) &&
                w.belongsToLine(lineInfo ? lineInfo.lineEnd : 0, addToOriginalDiff, lineInfo ? lineInfo.type : LineInfoType.Actual)
        )[0];

        if (!widget) {
            widget = this.createWidget({
                addToOriginalDiff,
                lineInfo,
                pageContext,
                renderContent: props => (
                    <Observer diffWidth={this.diffWidth}>
                        {() => {
                            return (
                                <CollapsibleDiscussionHost
                                    className={props.className}
                                    currentUser={currentUser}
                                    diffWidth={
                                        this.diffWidth?.value && this.props.diffOrientation
                                            ? this.diffWidth.value / 2
                                            : undefined
                                    }
                                    discussionStore={discussionStore}
                                    hasNoLineInfo={!lineInfo || lineInfo.lineEnd === SpecialCommentPositions.File}
                                    onContentLoaded={props.onContentLoaded}
                                    onRemoveThread={this.onRemoveThread}
                                    renderDiscussion={renderDiscussion}
                                    threads={props.threads}
                                />
                            );
                        }}
                    </Observer>
                )
            });

            this.widgets.push(widget);
        }

        widget.addThread(thread);
    }

    protected removeThreadFromWidgets(threadId: number): void {
        const widgetsToRemove = new Set<TWidgetType>();

        this.getWidgetsByThread(threadId).forEach(widget => {
            widget.removeThread(threadId);

            // If there are no thread left to show in the widget, remove also the widget
            if (!widget.hasThreads()) {
                widgetsToRemove.add(widget);
            }
        });

        if (widgetsToRemove.size > 0) {
            this.widgets.removeAll(testWidget => widgetsToRemove.has(testWidget));
        }
    }

    protected onRemoveThread = (threadId: number): void => {
        // Remove thread from the discussion thread store so that UI gets updated - onUpdateThreads will be called
        this.props.discussionStore.removeThread(threadId);
    };

    protected onStartThread = (lineInfo: ILineInfo, addToOriginalDiff: boolean): void => {
        const { createThreadModel, discussionStore, filePath, initializeThread } = this.props;

        const thread = createThreadModel();

        if (initializeThread) {
            initializeThread(thread, filePath, { addToOriginalDiff, lineInfo });
        }

        // Add thread to the discussion thread store so that UI gets updated - onUpdateThreads will be called
        discussionStore.addThread(thread);
    };

    protected filterThreads(threads: IDiscussionThreadModel[]): IDiscussionThreadModel[] {
        const { filePath, filterThreads } = this.props;
        return filterThreads(threads, filePath);
    }

    protected getWidgetIndexByThread(threadId: number, integrationContext?: IDiscussionIntegrationContext): number {
        return this.widgets.value.findIndex(w => this.widgetByThreadFilter(w, threadId, integrationContext));
    }

    protected getWidgetsByThread(threadId: number, integrationContext?: IDiscussionIntegrationContext): TWidgetType[] {
        return this.widgets.value.filter(w => this.widgetByThreadFilter(w, threadId, integrationContext));
    }

    protected widgetByThreadFilter = (widget: TWidgetType, threadId: number, integrationContext?: IDiscussionIntegrationContext) => {
        return (
            this.isMyWidgetInstance(widget) &&
            widget.containsThread(threadId) &&
            (!integrationContext ||
                widget.belongsToLine(
                    integrationContext.lineInfo ? integrationContext.lineInfo.lineEnd : SpecialCommentPositions.File,
                    integrationContext.addToOriginalDiff,
                    integrationContext.lineInfo ? integrationContext.lineInfo.type : LineInfoType.Actual
                ))
        );
    };

    private onUpdateThreads = (args: IObservableArrayEventArgs<IDiscussionThreadModel>): void => {
        const threadsToAdd: IDiscussionThreadModel[] = [];

        // Here is how we update threads:
        //
        // From the new threads list, see is there any thread we can update. We don't
        // want to remove/add already existing threads since the user may be in the
        // middle of editing and we don't want to get the changes lost (especially for the ones in Monaco).
        // While doing this, we keep a list of threads to be added
        const newThreads = this.filterThreads(args.addedItems || []);
        newThreads.forEach(thread => {
            const widgetIndex = this.getWidgetIndexByThread(thread.id, this.props.getIntegrationContext(thread));
            if (widgetIndex < 0) {
                threadsToAdd.push(thread);
            }
        });

        // Then from the remove list, see anything we want to add exist. This is
        // for the case where thread is still there but it's location is changed
        // between updates. We remove these threads since they will be added next.
        const oldThreads = this.filterThreads(args.removedItems || []);
        oldThreads.forEach(oldThread => {
            if (
                threadsToAdd.findIndex(threadToAdd => threadToAdd.id === oldThread.id) >= 0 ||
                newThreads.findIndex(newThread => newThread.id === oldThread.id) < 0
            ) {
                this.removeThreadFromWidgets(oldThread.id);
            }
        });

        // Finally, add the new threads and the ones which have different locations (line numbers)
        for (const thread of threadsToAdd) {
            this.addThreadToWidget(thread);
        }        

        this.props.onThreadsUpdated && this.props.onThreadsUpdated();
    };
}
